import React, { CSSProperties, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as Sentry from '@sentry/react';

import { Page, TransitionUrl, TranslationPath } from '../../../contexts/kioskConfiguration/type';
import { Footer } from '../../../layout/footer/Footer';
import { Card, DataList, Flex } from '@radix-ui/themes';
import useLanguage from '../../../contexts/language/LanguageContext';
import { RootState } from '../../../store/store';
import { getTranslationByPath } from '../../../utils/translations';
import { Transportation } from '../../../types/transportation';
import { Translation } from '../../../contexts/language/translationType';
import { getTransportation } from '../../../apollo/queries/getTransportation';
import { setSelectedPage } from '../../../store/slices/app/appSlice';


type CurrentStatusItem = {
  label: string
  property: string
  fontSize?: number
  textColor?: string
}

export type CurrentStatusTemplateType = {
  label: TranslationPath
  items: CurrentStatusItem[][]
  states: { [key: string]: TranslationPath }
  transitionUrl: TransitionUrl
}

type Props = {
  page: Page<CurrentStatusTemplateType>
}

const getValue = (
  item: CurrentStatusItem,
  transportation: Transportation,
  pageData: CurrentStatusTemplateType,
  translations: Translation
): string => {
  if (item.property === 'state') {
    return getTranslationByPath(translations, pageData.states[transportation.state])
  }
  const value = item.property in transportation ? transportation[item.property] : ''
  if (value === true) {
    return translations.general.yes
  }
  if (value === false) {
    return translations.general.no
  }
  if (value !== null && typeof value === 'string' && /\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d+[A-Z]+/.test(value)) {
    const d = new Date(value);
    return `${d.toLocaleDateString('cs-CZ', { timeZone: 'Europe/Prague' })} ${d.toLocaleString('cs-CZ', { timeZone: 'Europe/Prague', hour: '2-digit', minute: '2-digit' })}`
  }
  return value
}

type RenderValueProps = {
  item: CurrentStatusItem
  transportation: Transportation
  pageData: CurrentStatusTemplateType
  translations: Translation
  style: CSSProperties
}

const RenderValue = (props: RenderValueProps): React.ReactElement => {
  return (
    <>
      <DataList.Value className="font-bold text-2xl" style={props.style}>
        {getValue(props.item, props.transportation, props.pageData, props.translations)}
      </DataList.Value>
      {props.item.property === 'registrationNumber' && props.transportation.blacklist && (
        <DataList.Value className="font-bold text-2xl" style={props.style}>
          {props.translations.currentStatusPage.blacklistLabel}
        </DataList.Value>
      )}
      {props.item.property === 'registrationNumber' && props.transportation.customs && (
        <DataList.Value className="font-bold text-2xl" style={props.style}>
          {props.translations.currentStatusPage.customsLabel}
        </DataList.Value>
      )}
    </>
  )
}

export const CurrentStatusTemplate = (props: Props): React.ReactElement => {
  const language = useLanguage()
  const fetchedTransportation = useSelector((state: RootState) => state.transportation.fetchedTransportation)
  const appState = useSelector((state: RootState) => state.app)

  const dispatch = useDispatch()

  const [transportation, setTransportation] = useState<Transportation | null>(fetchedTransportation)

  useEffect(() => {
    const intervalId = setInterval(async () => {
      if (!transportation) {
        return
      }
      try {
        const findTransportationResult = await getTransportation({
          id: transportation.id,
          qtirLanguageId: language.qtirLanguageId,
        })

        const foundTransportation = findTransportationResult.data.getTransportation.transportation
        setTransportation(foundTransportation)
      } catch (e) {
        Sentry.captureException(e, { tags: { type: 'getTransportationInCurrentStatus', transportationId: transportation.id } })
      }
    }, 20000)

    return () => {
      clearInterval(intervalId)
    }
  })

  return <div>
    <h1 className="text-3xl text-center">{getTranslationByPath(language.translations, props.page.data.label)}</h1>

    <Card className="mt-8 flex flex-col">
      {
        transportation && props.page.data.items.map(items => {
          const children = items.map(item => {
            const style: CSSProperties = {}
            if (item.textColor) {
              style.color = item.textColor
            }
            if (item.fontSize) {
              style.fontSize = item.fontSize
            }
            if (item.property === 'queueOrder' && transportation[item.property] === null) {
              return null
            }
            return (
              <DataList.Root orientation="vertical" className="text-center mx-auto">
                <DataList.Item key={item.property} className="flex items-center mb-2">
                  <DataList.Label highContrast className="text-2xl">{getTranslationByPath(language.translations, item.label)}</DataList.Label>
                  <RenderValue style={style} item={item} transportation={transportation} pageData={props.page.data} translations={language.translations} />
                </DataList.Item>
              </DataList.Root>
            );
          }).filter(element => !!element)

          if (children.length === 0) {
            return null
          }
          return (
            <Flex className="justify-around mb-8">
              {children}
            </Flex>)
        })
      }
    </Card>

    <Footer
      backUrl={appState.selectedPageHistory[appState.selectedPageHistory.length - 1] !== '/registration' ? props.page.footer?.back : undefined}
      onContinue={appState.selectedPageHistory[appState.selectedPageHistory.length - 1] === '/registration' ? () => {
        dispatch(setSelectedPage(props.page.data.transitionUrl))
      } : undefined}
    />
  </div>
}
